/**
 * One starred row on the editable rail, with its right-click menu — and, for a
 * parent row, its sub-items indented below it (one level deep). The sub-items
 * area is a drop target, so a row dropped there joins them. On the expanded rail
 * a parent carries a chevron that folds its sub-items away. The icon-only rail
 * shows top-level rows only — sub-items hide there whatever their fold state, so
 * the icon column stays a short, scannable list. While a parent's sub-items are
 * hidden, either way, the parent lights for them, so the current page's row is
 * never out of sight. Every expanded row keeps the
 * chevron's column clear, chevron or not, so counts line up down the rail.
 */
import React from 'react';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Icons } from '@shared/components/ui';
import { destinationById, type Destination } from '@shared/navigation/destinations';
import { childrenOf, idOf, type RailLayout, type RowEntry } from '@shared/navigation/railLayout';

import { rowMenuItems, type RailActions } from './railMenus';
import { RailRow, SortableRailRow } from './RailRow';
import { useRailCollapseStore } from './useRailCollapseStore';

import type { RailNodeData } from './useRailDrag';
import type { NavCounts } from '@app/stores/slices/uiSlice';

/** What every row on the editable rail needs. */
export interface RailRowContext {
  layout: RailLayout;
  editMode: boolean;
  isCollapsed: boolean;
  counts: NavCounts;
  rowClick: { onClick?: () => void };
  actions: RailActions;
}

/** A row's menu, from the rail context. */
function menuFor(id: string, ctx: RailRowContext) {
  return rowMenuItems(
    ctx.layout,
    id,
    { editMode: ctx.editMode, isCollapsed: ctx.isCollapsed },
    ctx.actions
  );
}

/** A parent's sub-items on the expanded rail: indented, sortable among themselves, and a drop target. */
const SubItems: React.FC<{ parentId: string; ids: string[]; ctx: RailRowContext }> = ({
  parentId,
  ids,
  ctx,
}) => {
  const data: RailNodeData = { kind: 'rows', container: parentId };
  const { setNodeRef } = useDroppable({ id: `rows:${parentId}`, data });
  const rows = ids.map(id => destinationById(id)).filter((d): d is Destination => d !== undefined);
  return (
    <div ref={setNodeRef} data-testid="sub-items" className="pl-4">
      <SortableContext items={rows.map(d => d.id)} strategy={verticalListSortingStrategy}>
        {rows.map(d => (
          <SortableRailRow key={d.id} id={d.id} container={parentId}>
            <RailRow
              destination={d}
              isCollapsed={false}
              counts={ctx.counts}
              rowClick={ctx.rowClick}
              contextMenuItems={menuFor(d.id, ctx)}
              rightInset
            />
          </SortableRailRow>
        ))}
      </SortableContext>
    </div>
  );
};

/** The chevron overlaid on a parent row's right edge: right when folded, down when open. */
const FoldToggle: React.FC<{ label: string; folded: boolean; onToggle: () => void }> = ({
  label,
  folded,
  onToggle,
}) => {
  const Chevron = folded ? Icons.ChevronRight : Icons.ChevronDown;
  return (
    <button
      type="button"
      aria-expanded={!folded}
      aria-label={`${folded ? 'Show' : 'Hide'} ${label} sub-items`}
      onClick={onToggle}
      className="text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent absolute top-1/2 right-1 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded"
    >
      <Chevron size="xs" color="inherit" />
    </button>
  );
};

/** One row (and its sub-items), sitting in `container`. */
export const RailEntryView: React.FC<{
  entry: RowEntry;
  container: string;
  ctx: RailRowContext;
}> = ({ entry, container, ctx }) => {
  const id = idOf(entry);
  const folded = useRailCollapseStore(s => s.folded.includes(id));
  const toggleFolded = useRailCollapseStore(s => s.toggleFolded);
  const destination = destinationById(id);
  if (!destination) return null;
  const children = childrenOf(entry);
  const foldable = children.length > 0 && !ctx.isCollapsed;
  const showChildren = foldable && !folded;
  const hiddenChildren = children.length > 0 && !showChildren ? children : undefined;
  return (
    <SortableRailRow
      id={id}
      container={container}
      below={showChildren ? <SubItems parentId={id} ids={children} ctx={ctx} /> : null}
    >
      <div className="relative">
        <RailRow
          destination={destination}
          isCollapsed={ctx.isCollapsed}
          counts={ctx.counts}
          rowClick={ctx.rowClick}
          contextMenuItems={menuFor(id, ctx)}
          rightInset={!ctx.isCollapsed}
          {...(hiddenChildren && { activeFor: hiddenChildren })}
        />
        {foldable && (
          <FoldToggle
            label={destination.label}
            folded={folded}
            onToggle={() => toggleFolded(id)}
          />
        )}
      </div>
    </SortableRailRow>
  );
};
